import { useEffect, useState } from "react";
import { KeyRound, CheckCircle2, AlertCircle } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const inputCls = "border-white/15 bg-white/5 text-white";
const labelCls = "text-xs text-white/60";

const Status = ({ ok, testid }) => ok ? (
  <span className="flex items-center gap-1.5 text-xs font-medium text-emerald-400" data-testid={testid}><CheckCircle2 className="h-4 w-4" /> Configured</span>
) : (
  <span className="flex items-center gap-1.5 text-xs font-medium text-white/50" data-testid={testid}><AlertCircle className="h-4 w-4" /> Not set</span>
);

/**
 * BankingSettingsPanel — Settings → API: Plaid (bank connections) + VoPay
 * (e-Transfers / direct deposit) credentials for THIS workgroup. Secrets are
 * write-only: the server stores them encrypted and only reports "set".
 */
const BankingSettingsPanel = ({ workgroup }) => {
  const [status, setStatus] = useState(null);
  const [form, setForm] = useState({ plaid_client_id: "", plaid_secret: "", plaid_env: "sandbox", vopay_account_id: "", vopay_key: "", vopay_shared_secret: "", vopay_env: "sandbox" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/banking/settings").then((r) => {
      setStatus(r.data);
      setForm((s) => ({ ...s, plaid_env: r.data.plaid.env, vopay_env: r.data.vopay.env }));
    }).catch(() => {});
  }, []);

  const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/banking/settings", form);
      setStatus(data);
      setForm((s) => ({ ...s, plaid_secret: "", vopay_key: "", vopay_shared_secret: "" }));
      toast.success("Banking credentials saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const wgName = workgroup?.name || "this workgroup";
  const envPicker = (k, testid) => (
    <select value={form[k]} onChange={(e) => set(k, e.target.value)} data-testid={testid} className="h-9 rounded-md border border-white/15 bg-[#0a0a0a] px-2 text-sm text-white">
      <option value="sandbox">Sandbox (testing)</option>
      <option value="production">Production (live)</option>
    </select>
  );

  return (
    <div className="space-y-4" data-testid="banking-settings-panel">
      {/* -------------------- Plaid -------------------- */}
      <div className="mt-8 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
        <KeyRound className="h-4 w-4 text-sky-400" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-white">Plaid — bank connections</p>
          <p className="text-xs text-white/40">Lets the company and each employee link a Canadian bank to Accounting and pull transactions in.</p>
        </div>
        <Status ok={status?.plaid?.configured} testid="plaid-status" />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label className={labelCls}>Client ID {status?.plaid?.client_id_masked ? `(current: ${status.plaid.client_id_masked})` : ""}</Label>
          <Input value={form.plaid_client_id} onChange={(e) => set("plaid_client_id", e.target.value)} data-testid="plaid-client-id-input" className={inputCls} />
        </div>
        <div>
          <Label className={labelCls}>Secret {status?.plaid?.has_secret ? "(set — leave blank to keep)" : ""}</Label>
          <Input type="password" value={form.plaid_secret} onChange={(e) => set("plaid_secret", e.target.value)} data-testid="plaid-secret-input" className={inputCls} />
        </div>
        <div className="flex items-center gap-2"><Label className={labelCls}>Environment</Label>{envPicker("plaid_env", "plaid-env-select")}</div>
      </div>
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-white/40" data-testid="plaid-setup-help">
        <p className="mb-1 font-semibold text-white/70">How to get these ({wgName} needs its own Plaid account):</p>
        <ol className="list-decimal space-y-0.5 pl-4">
          <li>Sign up at <a href="https://dashboard.plaid.com/signup" target="_blank" rel="noreferrer" className="text-[#8fa4f0] underline">dashboard.plaid.com</a> with the company email.</li>
          <li><strong>Developers → Keys</strong>: copy the <em>client_id</em> and the <em>Sandbox</em> secret here to test (use Plaid's test bank with user <code>user_good</code> / <code>pass_good</code>).</li>
          <li>Enable <strong>Transactions</strong> and <strong>Auth</strong> products, country <strong>Canada</strong>, and request Production access. When approved, paste the <em>Production</em> secret and switch Environment to Production.</li>
          <li>Under <strong>Partners</strong> enable <strong>VoPay</strong> so direct deposits can use connected accounts.</li>
        </ol>
      </div>

      {/* -------------------- VoPay -------------------- */}
      <div className="mt-8 flex items-center gap-2 rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3">
        <KeyRound className="h-4 w-4 text-amber-400" />
        <div className="flex-1">
          <p className="text-sm font-semibold text-white">VoPay — e-Transfers &amp; direct deposit</p>
          <p className="text-xs text-white/40">Sends Interac e-Transfers and EFT direct deposits from the company account (Accounting → Send money).</p>
        </div>
        <Status ok={status?.vopay?.configured} testid="vopay-status" />
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <div>
          <Label className={labelCls}>Account ID {status?.vopay?.account_id_masked ? `(current: ${status.vopay.account_id_masked})` : ""}</Label>
          <Input value={form.vopay_account_id} onChange={(e) => set("vopay_account_id", e.target.value)} data-testid="vopay-account-id-input" className={inputCls} />
        </div>
        <div>
          <Label className={labelCls}>API Key {status?.vopay?.has_key ? "(set — leave blank to keep)" : ""}</Label>
          <Input type="password" value={form.vopay_key} onChange={(e) => set("vopay_key", e.target.value)} data-testid="vopay-key-input" className={inputCls} />
        </div>
        <div>
          <Label className={labelCls}>Shared Secret {status?.vopay?.has_shared_secret ? "(set — leave blank to keep)" : ""}</Label>
          <Input type="password" value={form.vopay_shared_secret} onChange={(e) => set("vopay_shared_secret", e.target.value)} data-testid="vopay-shared-secret-input" className={inputCls} />
        </div>
        <div className="flex items-center gap-2"><Label className={labelCls}>Environment</Label>{envPicker("vopay_env", "vopay-env-select")}</div>
      </div>
      <div className="rounded-lg border border-white/10 bg-white/[0.02] p-3 text-xs text-white/40" data-testid="vopay-setup-help">
        <p className="mb-1 font-semibold text-white/70">How to get these ({wgName} needs its own VoPay account):</p>
        <ol className="list-decimal space-y-0.5 pl-4">
          <li>Apply at <a href="https://vopay.com/en-ca/contact/" target="_blank" rel="noreferrer" className="text-[#8fa4f0] underline">vopay.com</a> for a business account — they onboard the company (KYC) and give sandbox access first.</li>
          <li>In the VoPay portal (<a href="https://docs.vopay.com/reference/getting-started-with-your-api" target="_blank" rel="noreferrer" className="text-[#8fa4f0] underline">API docs</a>) copy the <em>AccountID</em>, <em>API Key</em> and <em>Shared Secret</em> for Sandbox here.</li>
          <li>Fund the VoPay account from the company bank; payments are sent from that balance.</li>
          <li>After certification, paste the Production credentials and switch Environment to Production.</li>
        </ol>
      </div>

      <div className="flex justify-end">
        <Button onClick={save} disabled={saving} data-testid="save-banking-keys-btn" variant="outline" className="border-white/20 bg-transparent text-white hover:bg-white/10">
          {saving ? "Saving…" : "Save banking credentials"}
        </Button>
      </div>
    </div>
  );
};

export default BankingSettingsPanel;
